/**
 * Traducciones del juego. Sin dependencias: un diccionario por idioma y una
 * funcion `t()`.
 *
 * Decisiones que conviene conocer antes de tocar esto:
 *
 * - **`t()` es SINCRONA y el idioma se resuelve al cargar el modulo.** La
 *   interfaz se construye con plantillas de texto (`innerHTML = \`...\``), asi
 *   que una traduccion asincrona obligaria a reescribir cada pantalla. Cambiar
 *   de idioma en caliente recarga la pagina: es una vez por sesion y ahorra
 *   tener que re-renderizar todo a mano.
 * - **La clave ES el texto en español.** Sin claves abstractas tipo
 *   `menu.play.title`: el codigo se sigue leyendo, y una traduccion que falte
 *   cae al español en vez de mostrar un identificador feo al jugador.
 * - **El idioma NO entra en la simulacion.** Nada de esto viaja por el
 *   protocolo ni afecta al lockstep; dos jugadores con idiomas distintos ven el
 *   mismo partido.
 */
import { EN } from "./en.js";

export type Lang = "es" | "en";

const STORAGE_KEY = "fynta.lang";

/** Diccionarios. El español es la clave, asi que solo hay tabla para el resto. */
const TABLES: Record<Exclude<Lang, "es">, Record<string, string>> = { en: EN };

function detect(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "es" || saved === "en") return saved;
  } catch {
    // localStorage bloqueado (modo privado): se decide por el navegador.
  }
  const nav = typeof navigator !== "undefined" ? navigator.language : "";
  return nav.toLowerCase().startsWith("es") ? "es" : "en";
}

let current: Lang = detect();

export function lang(): Lang {
  return current;
}

/**
 * Traduce. El texto en español es la clave.
 *
 * `params` sustituye `{algo}` en la cadena, para lo que lleva numeros dentro
 * ("quedan {n} segundos"): partir la frase en trozos y concatenarla haria
 * imposible traducirla a un idioma con otro orden de palabras.
 */
export function t(es: string, params?: Record<string, string | number>): string {
  let out = current === "es" ? es : (TABLES[current]?.[es] ?? es);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      out = out.replaceAll(`{${k}}`, String(v));
    }
  }
  return out;
}

/**
 * Duracion del fundido a negro antes de recargar. Coincide con la transicion
 * de `#veil` en `index.html`; si se cambia una, hay que cambiar la otra o la
 * recarga se cuela a mitad del fundido.
 */


/** Abre el velo. Lo llama el arranque cuando el juego ya esta montado. */
export function revealApp(): void {
  document.getElementById("veil")?.classList.add("is-clear");
}

/**
 * Cambia el idioma. YA NO RECARGA.
 *
 * Recargaba porque `t()` es sincrona y cada pantalla cuece su texto al
 * construirse, asi que la unica forma barata de re-traducir todo era volver a
 * arrancar. Pero recargar cuesta el arranque entero —Pixi, texturas, audio— y
 * eso se ve como una pantalla de carga en mitad de un menu: parece que algo ha
 * fallado.
 *
 * Ahora solo cambia el idioma y avisa. Quien construye los menus (`main.ts`)
 * los DESTRUYE Y LOS VUELVE A CREAR, que es lo mismo que hacia la recarga con
 * la interfaz pero sin volver a montar el juego. Es instantaneo y no se sale
 * del sitio donde estabas.
 *
 * Devuelve `true` si de verdad ha cambiado, para no reconstruir de balde.
 */
export function setLang(next: Lang): boolean {
  if (next === current) return false;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Sin almacenamiento el cambio dura lo que la pestaña.
  }
  current = next;
  syncDocumentLang();
  return true;
}

/** Para el `lang` del <html>, que usan lectores de pantalla y buscadores. */
export function syncDocumentLang(): void {
  if (typeof document !== "undefined") document.documentElement.lang = current;
}
